import React, { useState, useEffect } from "react";
import {
  Modal,
  Input,
  Button,
  Space,
  Typography,
  message as antMessage,
  Tag,
  Tooltip,
  Skeleton,
} from "antd";
import { ShoppingCartOutlined, ReloadOutlined } from "@ant-design/icons";
import Table from "../../../core/common/dataTable/index";
import { useDispatch, useSelector } from "react-redux";
import "./agents.css";
import { all_routes } from "../../router/all_routes";
import api from "../../../core/axios/axiosInstance";
import AvatarInitialStyles from "../../../core/common/nameInitialStyles/AvatarInitialStyles";
import { showToast } from "../../../core/data/redux/slices/ToastSlice";
import { fetchAgentQuota } from "../../../core/data/redux/slices/BillingSlice";
import { Link, useNavigate } from "react-router-dom";
import AgentPermissionOffcanvas from "../../../core/common/offCanvas/permission/PermissionOffcanvas";
import TableSkeleton from "../../../core/common/TableSkeleton";

let _agentsCache = { data: null };

const { Text } = Typography;

const Agents = () => {
  const route = all_routes;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [agents, setAgents] = useState(_agentsCache.data || []);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(!_agentsCache.data);
  const [refreshing, setRefreshing] = useState(false);
  const [addAgentModal, setAddAgentModal] = useState(false);
  const [editAgentModal, setEditAgentModal] = useState(false);
  const [deleteAgentModal, setDeleteAgentModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showPermissionOffcanvas, setShowPermissionOffcanvas] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    firstname: "",
    lastname: "",
  });
  const [formErrors, setFormErrors] = useState({});

  // ── Agent quota ──────────────────────────────────────────────────────────────
  const agentQuota        = useSelector((state) => state.billing?.agentQuota);
  const agentQuotaLoading = useSelector((state) => state.billing?.agentQuotaLoading);
  const profile           = useSelector((state) => state.profile);

  // Admin always occupies 1 seat; remaining seats available for new agents
  const availableSeats = agentQuota ? Math.max(0, agentQuota.remaining - 1) : null;
  // Only consider exhausted once we have a definitive answer from the server
  const quotaReady     = !agentQuotaLoading && agentQuota !== null;
  const seatsExhausted = quotaReady && availableSeats === 0;

  const handleEditPermissions = (agent) => {
    setSelectedAgent(agent || null);
    setShowPermissionOffcanvas(true);
  };

  useEffect(() => {
    fetchAgents();
    dispatch(fetchAgentQuota());
  }, []);

  const fetchAgents = async (forceRefresh = false) => {
    if (!forceRefresh && _agentsCache.data) {
      setAgents(_agentsCache.data);
      setIsInitialLoad(false);
      return;
    }
    if (forceRefresh) _agentsCache = { data: null };
    setIsInitialLoad(true);
    setRefreshing(forceRefresh);
    setLoading(true);
    try {
      const response = await api.get("/admin/user/getAgent");
      if (response.data.status === "success") {
        setAgents(response.data.data);
        _agentsCache = { data: response.data.data };
      }
    } catch (error) {
      dispatch(
        showToast({
          message: "Failed to fetch agents",
          variant: "danger",
        })
      );
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
      setRefreshing(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!formData.email) {
      errors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email is invalid";
      isValid = false;
    }

    if (!formData.firstname) {
      errors.firstname = "First name is required";
      isValid = false;
    }

    if (!formData.lastname) {
      errors.lastname = "Last name is required";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleAddAgent = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post("/admin/user/register", formData);

      if (response.data.status === "success") {
        antMessage.success(
          "Agent created successfully! Verification email sent."
        );
        setAddAgentModal(false);
        setFormData({ email: "", firstname: "", lastname: "" });
        fetchAgents(true);
      } else {
        antMessage.error(response.data.message || "Failed to create agent");
      }
    } catch (error) {
      console.error("Error creating agent:", error);
      antMessage.error(
        error.response?.data?.message ||
        "Failed to create agent. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };
const handleResendVerification = async (agentId, email) => {
  try {
    antMessage.info(`Resending verification email to ${email}`);
    console.log("Resending verification email to...", email);

    const response = await api.post("user/resendVerificationLink", {
      email
    });

    console.log(response.data, "response from resend verification link");

    antMessage.success("Verification email sent successfully");
  } catch (error) {
    console.error(error);
    antMessage.error("Failed to resend verification email");
  }
};

  const handleOpenEditModal = (agent) => {
    setSelectedAgent(agent);
    setFormData({
      email: agent.email || "",
      firstname: agent.firstname || "",
      lastname: agent.lastname || "",
    });
    setFormErrors({});
    setEditAgentModal(true);
  };

  const handleEditAgent = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.put(
        `/admin/user/${selectedAgent._id}`,
        formData
      );

      if (response.data.status === "success") {
        antMessage.success("Agent updated successfully!");
        setEditAgentModal(false);
        setFormData({ email: "", firstname: "", lastname: "" });
        setSelectedAgent(null);
        fetchAgents(true);
      } else {
        antMessage.error(response.data.message || "Failed to update agent");
      }
    } catch (error) {
      console.error("Error updating agent:", error);
      antMessage.error(
        error.response?.data?.message ||
        "Failed to update agent. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenDeleteModal = (agent) => {
    setSelectedAgent(agent);
    setDeleteAgentModal(true);
  };

  const handleDeleteAgent = async () => {
    if (!selectedAgent) return;

    setIsSubmitting(true);
    try {
      const response = await api.delete(`/admin/user/${selectedAgent._id}`);

      if (response.data.status === "success") {
        antMessage.success("Agent deleted successfully!");
        setDeleteAgentModal(false);
        setSelectedAgent(null);
        fetchAgents(true);
      } else {
        antMessage.error(response.data.message || "Failed to delete agent");
      }
    } catch (error) {
      console.error("Error deleting agent:", error);
      antMessage.error(
        error.response?.data?.message ||
        "Failed to delete agent. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const adminRow = {
    _id: profile._id || 'admin-row',
    firstname: profile.firstname,
    lastname: profile.lastname,
    email: profile.email,
    PBXDetails: { PBX_EXTENSION_NUMBER: profile.extensionNumber || '' },
    isVerified: true,
    isActive: true,
    yeastarProvisionStatus: profile.yeastarProvisionStatus || 'pending',
    createdAt: profile.createdAt,
    isAdmin: true,
  };
  const tableData = [adminRow, ...agents];

  const columns = [
    {
      title: "Name",
      dataIndex: "firstname",
      render: (text, record) => (
        <div className="d-flex align-items-center">
          <AvatarInitialStyles
            name={
              `${record.firstname || ""} ${record.lastname || ""}`.trim() ||
              record.email
            }
          />
          <div className="ms-2">
            <div className="fw-semibold">
              {`${record.firstname || ""} ${record.lastname || ""}`.trim() || record.email || "Not set"}
            </div>
          </div>
        </div>
      ),
      sorter: (a, b) => {
        const nameA = `${a.firstname || ""} ${a.lastname || ""}`.trim();
        const nameB = `${b.firstname || ""} ${b.lastname || ""}`.trim();
        return nameA.localeCompare(nameB);
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: "Extension",
      dataIndex: "extensionNumber",
      render: (text, record) => {
        const ext = record.PBXDetails?.PBX_EXTENSION_NUMBER || text;
        return ext ? (
          <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#374151" }}>{ext}</span>
        ) : (
          <span className="text-muted">—</span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "isVerified",
      render: (isVerified, record) => {
        if (isVerified && record.isActive) {
          return <Tag color="success">Active</Tag>;
        } else if (isVerified && !record.isActive) {
          return <Tag color="warning">Inactive</Tag>;
        } else {
          return <Tag color="default">Pending Verification</Tag>;
        }
      },
      filters: [
        { text: "Active", value: "active" },
        { text: "Inactive", value: "inactive" },
        { text: "Pending Verification", value: "pending" },
      ],
      onFilter: (value, record) => {
        if (value === "active") return record.isVerified && record.isActive;
        if (value === "inactive") return record.isVerified && !record.isActive;
        if (value === "pending") return !record.isVerified;
        return true;
      },
    },
    {
      title: "PBX Status",
      dataIndex: "yeastarProvisionStatus",
      render: (status) => {
        if (status === "done") {
          return <Tag color="success">Provisioned</Tag>;
        } else if (status === "failed") {
          return <Tag color="error">Failed</Tag>;
        } else if (status === "pending") {
          return <Tag color="processing">Pending</Tag>;
        } else {
          return <Tag color="default">Not Started</Tag>;
        }
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text, record) => {
        if (record.isAdmin) {
          return <Tag color="purple">Company Admin</Tag>;
        }
        return (
        <div className="action-icons d-flex gap-2">
          <Button
            type="link"
            size="small"
            // data-bs-toggle="offcanvas"
            // data-bs-target="#agent_permission_offcanvas"
            icon={<i className="ti ti-edit" />}
            onClick={() => handleEditPermissions(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            size="small"
            danger
            icon={<i className="ti ti-trash" />}
            onClick={() => handleOpenDeleteModal(record)}
          >
            Delete
          </Button>
          {!record.isVerified && (
            <Button
              type="link"
              size="small"
              onClick={() => handleResendVerification(record._id, record.email)}
            >
              Resend Email
            </Button>
          )}
        </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-sm-12">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="page-header mb-0">
                          <div className="row align-items-center">
                            <h4 className="page-title mb-0">
                              Agent Management
                            </h4>
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() => fetchAgents(true)}
                            disabled={refreshing}
                          >
                            {refreshing ? (
                              <span className="spinner-border spinner-border-sm me-1" />
                            ) : (
                              <ReloadOutlined className="me-1" />
                            )}
                            Refresh
                          </button>
                          {/* While quota is loading — show a skeleton so the button
                              never flashes in before we know the seat status */}
                          {!quotaReady ? (
                            <Skeleton.Button active size="default" style={{ width: 110 }} />
                          ) : (
                            <>
                              {/* Seat usage badge */}
                              <span style={{
                                fontSize: 12, fontWeight: 600, padding: "3px 10px",
                                borderRadius: 20,
                                background: seatsExhausted ? "#fee2e2" : "#f0fdf4",
                                color:      seatsExhausted ? "#b91c1c"  : "#15803d",
                                border:     `1px solid ${seatsExhausted ? "#fca5a5" : "#86efac"}`,
                              }}>
                                {(agentQuota.usedCount + 1)} / {agentQuota.agentCount} seats used
                              </span>

                              {seatsExhausted ? (
                                <Tooltip title="All agent seats are in use. Purchase more seats to add agents.">
                                  <Button
                                    type="primary"
                                    danger
                                    icon={<ShoppingCartOutlined />}
                                    onClick={() => navigate(route.billing)}
                                  >
                                    Buy Seats
                                  </Button>
                                </Tooltip>
                              ) : (
                                <Button
                                  type="primary"
                                  onClick={() => handleEditPermissions()}
                                >
                                  <i className="ti ti-plus me-2" />
                                  Add Agent
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="table-responsive">
                    {isInitialLoad ? (
                      <TableSkeleton
                        rows={8}
                        rowHeight={52}
                        columns={[
                          { width: 180, type: "text" },
                          { width: 200, type: "text" },
                          { width: 100, type: "text" },
                          { width: 100, type: "badge" },
                          { width: 100, type: "badge" },
                          { width: 100, type: "text" },
                          { width: 120, type: "action" },
                        ]}
                      />
                    ) : (
                      <Table
                        columns={columns}
                        dataSource={tableData}
                        loading={loading}
                        rowKey="_id"
                        pagination={{
                          pageSize: 10,
                          showSizeChanger: true,
                          showTotal: (total, range) =>
                            `Showing ${range[0]} to ${range[1]} of ${total} entries`,
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AgentPermissionOffcanvas selectedAgent={selectedAgent} fetchAgents={fetchAgents}  show={showPermissionOffcanvas}
  onClose={() => setShowPermissionOffcanvas(false)} />
      <Modal
        title="Add New Agent"
        centered
        open={addAgentModal}
        onCancel={() => {
          setAddAgentModal(false);
          setFormData({ email: "", firstname: "", lastname: "" });
          setFormErrors({});
        }}
        footer={null}
        width={500}
      >
        <div style={{ padding: "20px 0" }}>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                Email <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                size="large"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter agent's email"
                status={formErrors.email ? "error" : ""}
              />
              {formErrors.email && (
                <Text type="danger" style={{ fontSize: "12px" }}>
                  {formErrors.email}
                </Text>
              )}
              <Text
                type="secondary"
                style={{ fontSize: "12px", display: "block", marginTop: "4px" }}
              >
                Must use company or custom domain email
              </Text>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                First Name <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                size="large"
                name="firstname"
                value={formData.firstname}
                onChange={handleInputChange}
                placeholder="Enter first name"
                maxLength={50}
                status={formErrors.firstname ? "error" : ""}
              />
              {formErrors.firstname && (
                <Text type="danger" style={{ fontSize: "12px" }}>
                  {formErrors.firstname}
                </Text>
              )}
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                Last Name <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                size="large"
                name="lastname"
                value={formData.lastname}
                onChange={handleInputChange}
                placeholder="Enter last name"
                maxLength={50}
                status={formErrors.lastname ? "error" : ""}
              />
              {formErrors.lastname && (
                <Text type="danger" style={{ fontSize: "12px" }}>
                  {formErrors.lastname}
                </Text>
              )}
            </div>

            <Button
              type="primary"
              size="large"
              block
              onClick={handleAddAgent}
              loading={isSubmitting}
              style={{ marginTop: "10px" }}
            >
              Create Agent & Send Invitation
            </Button>
          </Space>
        </div>
      </Modal>

      {/* Edit Agent Modal */}
      <Modal
        title="Edit Agent"
        centered
        open={editAgentModal}
        onCancel={() => {
          setEditAgentModal(false);
          setFormData({ email: "", firstname: "", lastname: "" });
          setFormErrors({});
          setSelectedAgent(null);
        }}
        footer={null}
        width={500}
      >
        <div style={{ padding: "20px 0" }}>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                Email <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                size="large"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter agent's email"
                status={formErrors.email ? "error" : ""}
              />
              {formErrors.email && (
                <Text type="danger" style={{ fontSize: "12px" }}>
                  {formErrors.email}
                </Text>
              )}
              <Text
                type="secondary"
                style={{ fontSize: "12px", display: "block", marginTop: "4px" }}
              >
                Must use company or custom domain email
              </Text>
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                First Name <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                size="large"
                name="firstname"
                value={formData.firstname}
                onChange={handleInputChange}
                placeholder="Enter first name"
                maxLength={50}
                status={formErrors.firstname ? "error" : ""}
              />
              {formErrors.firstname && (
                <Text type="danger" style={{ fontSize: "12px" }}>
                  {formErrors.firstname}
                </Text>
              )}
            </div>

            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "500",
                }}
              >
                Last Name <span style={{ color: "red" }}>*</span>
              </label>
              <Input
                size="large"
                name="lastname"
                value={formData.lastname}
                onChange={handleInputChange}
                placeholder="Enter last name"
                maxLength={50}
                status={formErrors.lastname ? "error" : ""}
              />
              {formErrors.lastname && (
                <Text type="danger" style={{ fontSize: "12px" }}>
                  {formErrors.lastname}
                </Text>
              )}
            </div>

            <Button
              type="primary"
              size="large"
              block
              onClick={handleEditAgent}
              loading={isSubmitting}
              style={{ marginTop: "10px" }}
            >
              Update Agent
            </Button>
          </Space>
        </div>
      </Modal>

      {/* Delete Agent Modal */}
      <Modal
        title="Delete Agent"
        centered
        open={deleteAgentModal}
        onCancel={() => {
          setDeleteAgentModal(false);
          setSelectedAgent(null);
        }}
        footer={null}
        width={500}
      >
        <div style={{ padding: "20px 0" }}>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <div>
              <Text style={{ fontSize: "16px" }}>
                Are you sure you want to delete agent{" "}
                <strong>
                  {selectedAgent?.firstname} {selectedAgent?.lastname}
                </strong>{" "}
                ({selectedAgent?.email})?
              </Text>
              <br />
              <br />
              <Text type="danger" style={{ fontSize: "14px" }}>
                This action cannot be undone. The agent's extension will
                also be deleted.
              </Text>
            </div>

            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button
                size="large"
                onClick={() => {
                  setDeleteAgentModal(false);
                  setSelectedAgent(null);
                }}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                // danger
                className="bg-theme"
                size="large"
                onClick={handleDeleteAgent}
                loading={isSubmitting}
              >
                Delete Agent
              </Button>
            </Space>
          </Space>
        </div>
      </Modal>
    </>
  );
};

export default Agents;
